/**
 * When to offer "set up a passkey" after someone signs in.
 *
 * Kept as a pure predicate plus thin storage helpers, following
 * utils/verificationPrompt.ts — the same shape the phone-verification prompt
 * uses, so the decision is testable without rendering anything.
 */

/** How long "Not now" holds the prompt off for. */
export const SNOOZE_DAYS = 30;

const KEY_PREFIX = 'passkey_prompt_snoozed_until';

/**
 * Per-user, per-browser. Both halves matter: shared machines are normal here,
 * so one account's dismissal must not silence another's; and a passkey is
 * per-device, so dismissing on a laptop should still leave the prompt to run
 * on a phone.
 */
export const passkeySnoozeKey = (userId?: number | string) =>
  `${KEY_PREFIX}_${userId}`;

export interface PasskeyPromptInput {
  /** Courier setting: is passkey sign-in switched on for this tenant. */
  isPasskeyEnabled?: boolean;
  /** Can this browser run a WebAuthn ceremony at all. */
  isSupported: boolean;
  /** Has the credential list actually resolved yet. */
  credentialsLoaded: boolean;
  /** Does the user already have at least one passkey. */
  hasCredentials: boolean;
  /** When the user last said "Not now", if ever. */
  snoozedUntil: Date | null;
  now: Date;
}

/**
 * Note there is no "did they sign in with a password?" input: having zero
 * credentials already implies they did not just sign in with a passkey, so the
 * auth slice needs no login-method flag.
 */
export const shouldPromptPasskeyEnrolment = ({
  isPasskeyEnabled,
  isSupported,
  credentialsLoaded,
  hasCredentials,
  snoozedUntil,
  now,
}: PasskeyPromptInput): boolean => {
  if (!isPasskeyEnabled) return false;
  if (!isSupported) return false;
  // Don't flash the modal at someone who turns out to already have one.
  if (!credentialsLoaded) return false;
  if (hasCredentials) return false;
  if (snoozedUntil && snoozedUntil > now) return false;

  return true;
};

export const readSnooze = (userId?: number | string): Date | null => {
  if (!userId) return null;

  try {
    const raw = localStorage.getItem(passkeySnoozeKey(userId));
    if (!raw) return null;

    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  } catch (err) {
    return null;
  }
};

export const writeSnooze = (userId?: number | string): void => {
  if (!userId) return;

  try {
    const until = new Date(Date.now() + SNOOZE_DAYS * 24 * 60 * 60 * 1000);
    localStorage.setItem(passkeySnoozeKey(userId), until.toISOString());
  } catch (err) {
    // A full or blocked localStorage just means we ask again next time.
  }
};

export const clearSnooze = (userId?: number | string): void => {
  if (!userId) return;

  try {
    localStorage.removeItem(passkeySnoozeKey(userId));
  } catch (err) {
    // Nothing to do — the snooze expires on its own.
  }
};
